import React from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import {
  Button,
  Card,
  Col,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Input,
  Label,
  Row,
  Table
} from 'reactstrap';

import styles from './style.module.scss';

import InfoIcon from '../../../assets/images/icons/info.svg';
import MoreIcon from '../../../assets/images/icons/more.svg';

import Environment from '../../../environment/Environment';
import { GetPlanId, NumberWithCommas, TwoDecimalPlace } from '../../../helpers/Helpers';

import SuccessModal from '../../../components/Common/Modals/SuccessModal';
import Pagination from '../../../components/Common/pagination/pagination';
import Tooltip from '../../../components/Common/tooltip/tooltip';

import { UpdateEditRestriction } from '../../../store/action-restriction/actions';

import { IManageProductData, IManageProductRow, ISavePH } from '../../../interfaces/manage-product.interface';
import { IColWidthPayload, IMeasureVisibilityPayload } from '../../../interfaces/common.interface';
import {
  GetManageProductData,
  InitialiseActive,
  InitialiseActiveAndRRView,
  SavePHList,
  UpdateActive, UpdateActiveAll, UnassignActiveAll,
  CopyWeightAndActiveStatus
} from '../../../api-services/manage-product.api';
import { UpdateColSequence, UpdateColWidth, UpdateMeasureVisibility } from '../../../api-services/common.api';

interface OwnProps {
  history: any;
  isCallRequired: boolean;
  onLoadEnd: () => void;
  onUpdateStart: () => void;
  onUpdateEnd: () => void;
}

interface StateProps {
  history: any;
  isAllowed: boolean;
  filterData: any;
}

interface DispatchProps {
  UpdateEditRestriction: (isAllowed: boolean) => void;
}

type Props = StateProps & DispatchProps & OwnProps;

interface State {
  isLoading: boolean;
  originalDataSource: IManageProductData;
  dataSource: IManageProductData;
  isModalOpened: boolean;
  itemsCount: number;
  itemsPerPage: number;
  currentPage: number;
  totalPages: number;
  isMoreMenuOpened: boolean;
  sortKey: string;
  sortDirection: string;
  isVisibilityMenuOpened: boolean;
  measureVisibilityList: any[];
  drag: {
    iniMouse: number;
    iniSize: number;
  };
  width: any;
  draggedPosition: number;
}

class ManageProductTable extends React.Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      isLoading: true,
      originalDataSource: { columns: [], rows: [] },
      dataSource: { columns: [], rows: [] },
      isModalOpened: false,
      itemsCount: 0,
      itemsPerPage: Environment.ITEMS_PER_PAGE,
      currentPage: 1,
      totalPages: 0,
      isMoreMenuOpened: false,
      sortKey: '',
      sortDirection: 'asc',
      isVisibilityMenuOpened: false,
      measureVisibilityList: [],
      drag: {
        iniMouse: 0,
        iniSize: 0
      },
      width: 0,
      draggedPosition: 0,
    };

    this.toggleMoreMenu = this.toggleMoreMenu.bind(this);
    this.toggleVisibilityMenu = this.toggleVisibilityMenu.bind(this);
  }

  componentDidMount(): void {
    this.getManageProduct();
    this.setState({
      width: document.getElementById('manage-product-table')?.offsetWidth || 0
    });
  }

  componentDidUpdate(prevProps, prevState): void {
    if (!prevProps.isCallRequired && this.props.isCallRequired) {
      this.setState({
        isLoading: true
      }, () => {
        this.getManageProduct();
      });
    }
  }

  getManageProduct(): void {
    const params = {
      userId: sessionStorage.getItem('userName'),
      planId: GetPlanId(this.props.history.location.pathname)
    };

    GetManageProductData(params)
      .then((res: IManageProductData) => {
        const items = (!!res && Object.keys(res).length !== 0)
          ? {
            columns: res.columns.map((col, colIdx: number) => {
              return {
                ...col,
                condition: '=',
                value: '',
                isVisible: col.isVisible,
                prevCheck: col.isVisible,
                width: res.columns[colIdx]?.width || 0
              };
            }),
            rows: res.rows
              .map(({ isVisible, ...keepAttrs }) => keepAttrs)
              .map((row) => {
                return {
                  ...row,
                  isNew: false,
                  isChecked: row.active_flag,
                  prevCheck: row.active_flag,
                  asp: TwoDecimalPlace(row.asp),
                  lqActuals$: TwoDecimalPlace(row.lq_qty_actuals),
                  lyActuals$: TwoDecimalPlace(row.ly_qty_actuals),
                  lyMargin$: TwoDecimalPlace(row.margin_ly),
                  yMarginPer: TwoDecimalPlace(row.margin_pct_ly),
                };
              })
          }
          : { columns: [], rows: [] };

        this.setState({
          originalDataSource: items,
          isLoading: false
        }, () => {
          this.dataInit();
        });

        this.props.onLoadEnd();
        this.props.UpdateEditRestriction(false);
      });
  }

  dataInit(): void {
    this.state.originalDataSource.columns.sort((x, y) => x.position - y.position);

    let items = this.state.originalDataSource.rows;
    const filteredColumnList = this.state.originalDataSource.columns
      .filter((col) => !!col.value);

    filteredColumnList.forEach((data) => {
      items = items.filter((item) => {
        if (!!data.value) {
          const matches = typeof item[data.id] === 'string'
            && item[data.id].toLocaleUpperCase().match(data.value.toLocaleUpperCase());
          return typeof item[data.id] === 'string'
            ? !!(matches && matches.length > 0)
            : (!!data.value ? ManageProductTable.aggregateFn(data.condition, item[data.id], parseFloat(data.value)) : true);
        }

        return false;
      });
    });

    if (!!this.state.sortKey) {
      items.sort((x, y) => {
        const xField = !!x[this.state.sortKey]
          ? (typeof x[this.state.sortKey] === 'string' ? x[this.state.sortKey].toLocaleUpperCase() : x[this.state.sortKey])
          : '';
        const yField = !!y[this.state.sortKey]
          ? (typeof y[this.state.sortKey] === 'string' ? y[this.state.sortKey].toLocaleUpperCase() : y[this.state.sortKey])
          : '';

        if (xField === yField) {
          return 0;
        }
        if (this.state.sortDirection === 'asc' && xField < yField) {
          return -1;
        }
        if (this.state.sortDirection === 'desc' && xField > yField) {
          return -1;
        }

        return 1;
      });
    }

    const itemsCount = items.length;
    items = items.slice(this.state.itemsPerPage * (this.state.currentPage - 1), this.state.itemsPerPage * this.state.currentPage);

    this.setState({
      itemsCount,
      totalPages: Math.ceil(this.state.originalDataSource.rows.length / this.state.itemsPerPage),
      dataSource: {
        columns: this.state.originalDataSource.columns,
        rows: items
      }
    }, () => {
      this.getVisibilityList();
    });
  }

  getVisibilityList(): void {
    const measureVisibilityList = this.state.originalDataSource.columns
      .map((col) => {
        return {
          ...col,
          prevCheck: col.prevCheck === undefined ? col.isVisible : col.prevCheck
        };
      });

    this.setState({ measureVisibilityList });
  }

  toggleVisibilityMenu(): void {
    this.setState((prevState) => ({
      isVisibilityMenuOpened: !prevState.isVisibilityMenuOpened
    }));
  }

  onUpdateCheckbox(rowId: number): void {
    this.state.dataSource.rows[rowId].isChecked = !this.state.dataSource.rows[rowId].isChecked;

    this.setState({
      dataSource: this.state.dataSource
    });
  }

  addRow(rowIdx: number): void {
    const data: IManageProductRow = this.state.dataSource.rows[rowIdx];
    const rowData = {
      ...data,
      product_code: `PH${this.getPHCount() + 1}-${data.product_code}`,
      product_description: '',
      isNew: true
    };
    this.state.dataSource.rows.splice(rowIdx + 1, 0, rowData);

    this.setState({
      dataSource: this.state.dataSource
    });
  }

  removeRow(rowIdx: number): void {
    this.state.dataSource.rows.splice(rowIdx, 1);

    this.setState({
      dataSource: this.state.dataSource
    });
  }

  checkLYLQValue(rowIdx): boolean {
    const rowData = this.state.dataSource.rows[rowIdx];
    return rowData.ly_qty_actuals === 0 &&
      rowData.lq_qty_actuals === 0 &&
      rowData.sale_net_val_ly === 0 &&
      rowData.sale_net_val_lq === 0 &&
      rowData.margin_ly === 0;
  }

  updateList(event, rowIdx: number, field: string): void {
    this.state.dataSource.rows[rowIdx][field] = event.target.value;

    this.setState({
      dataSource: this.state.dataSource
    });
  }

  getPHCount(): number {
    return this.state.originalDataSource.rows.reduce((acc, value) => {
      acc += !value.isSku ? 1 : 0;
      return acc;
    }, 0);
  }

  saveActiveValidity(): boolean {
    return this.state.dataSource.rows.filter((row) => row.isChecked !== row.prevCheck).length > 0;
  }

  enableDisableAssignAllAndAssignActiveBtn(): boolean {
    return this.state.dataSource.rows.filter((row) => row.allowdeactive).length > 0;
  }

  saveButtonValidity(): boolean {
    return this.state.dataSource.rows
        .filter((row) => row.isNew).length > 0 &&
      this.state.dataSource.rows
        .filter((row) => row.isNew)
        .reduce((acc, value) => acc && !!value.product_description, true);
  }

  navigateToProductCatalog(): void {
    this.props.history.replace('/manage-single-select-product/digital-single-select-product-catalog');
  }

  updateActiveAll(): void {
    const params = {
      userId: sessionStorage.getItem('userName'),
      planId: GetPlanId(this.props.history.location.pathname)
    };

    this.props.onUpdateStart();

    UpdateActiveAll(params)
      .then((res) => {
        if (!!res) {
          this.setState({
            isModalOpened: true
          }, () => {
            this.props.onUpdateEnd();
            this.props.UpdateEditRestriction(false);
          });
        }
      });
  }

  UnassignActiveAll(): void {
    const params = {
      userId: sessionStorage.getItem('userName'),
      planId: GetPlanId(this.props.history.location.pathname)
    };

    this.props.onUpdateStart();

    UnassignActiveAll(params)
      .then((res) => {
        if (!!res) {
          this.setState({
            isModalOpened: true
          }, () => {
            this.props.onUpdateEnd();
            this.props.UpdateEditRestriction(false);
          });
        }
      });
  }

  initialiseActive(): void {
    const params = {
      userId: sessionStorage.getItem('userName'),
      planId: GetPlanId(this.props.history.location.pathname)
    };

    this.props.onUpdateStart();

    InitialiseActive(params)
      .then((res) => {
        if (!!res) {
          this.setState({
            isModalOpened: true
          }, () => {
            this.props.onUpdateEnd();
            this.props.UpdateEditRestriction(false);
          });
        }
      });
  }

  initialiseActiveAndInitializeRunRateView(): void {
    const payload: {rows: IManageProductRow[]} = {
      rows: this.state.dataSource.rows.filter((row) => row.isChecked !== row.prevCheck)
    };

    const params = {
      userId: sessionStorage.getItem('userName'),
      planId: GetPlanId(this.props.history.location.pathname)
    };

    this.props.onUpdateStart();

    InitialiseActiveAndRRView(payload,params)
      .then((res) => {
        if (!!res) {
          this.setState({
            isModalOpened: true
          }, () => {
            this.props.onUpdateEnd();
            this.props.UpdateEditRestriction(false);
          });
        }
      });
  }

  updateActive(): void {
    const payload: {rows: IManageProductRow[]} = {
      rows: this.state.dataSource.rows.filter((row) => row.isChecked !== row.prevCheck)
    };

    const params = {
      userId: sessionStorage.getItem('userName'),
      planId: GetPlanId(this.props.history.location.pathname)
    };

    this.props.onUpdateStart();

    UpdateActive(payload, params)
      .then((res) => {
        if (!!res) {
          this.setState({
            isModalOpened: true
          }, () => {
            this.props.onUpdateEnd();
            this.props.UpdateEditRestriction(false);
          });
        }
      });
  }

  saveHandler(): void {
    const payload: ISavePH[] = this.state.dataSource.rows
      .filter((row) => row.isNew)
      .map((row) => {
        return {
          product_code: row.product_code,
          product_description: row.product_description
        };
      });

    const params = {
      userId: sessionStorage.getItem('userName'),
      planId: GetPlanId(this.props.history.location.pathname)
    };

    this.props.onUpdateStart();

    SavePHList(payload, params)
      .then((res) => {
        if (!!res) {
          this.setState({
            isModalOpened: true,
            currentPage: 1
          }, () => {
            this.props.onUpdateEnd();
            this.props.UpdateEditRestriction(false);
          });
        }
      });
  }

  copyWeightAndActiveStatus(): void {
    const params = {
      userId: sessionStorage.getItem('userName'),
      planId: GetPlanId(this.props.history.location.pathname)
    };

    this.props.onUpdateStart();

    CopyWeightAndActiveStatus(params)
      .then((res) => {
        if (!!res) {
          this.setState({
            isModalOpened: true
          }, () => {
            this.props.onUpdateEnd();
            this.props.UpdateEditRestriction(false);
          });
        }
      });
  }

  static addColumnStyle(rowStr: string): Object {
    return [
      'product_code',
      'product_description',
      'sku_type',
      'reference_sku_code',
      'actual_sku',
      'prod_level3_code',
      'prod_level4_code',
      'brand_level2_desc',
      'new_product_date',
      'discontinued_date',
      'graphics',
      'graphics_memory',
      'processor',
      'storage_size',
    ].indexOf(rowStr) !== -1
      ? {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }
      : {};
  }

  handlePageChange(pageNumber: number): void {
    this.setState({
      currentPage: pageNumber
    }, () => {
      this.dataInit();
    });
  }

  toggleMoreMenu(): void {
    this.setState((prevState) => ({
      isMoreMenuOpened: !prevState.isMoreMenuOpened
    }));
  }

  doSort(columnName: string): void {
    this.setState({
      sortKey: columnName || '',
      sortDirection: columnName === this.state.sortKey
        ? (this.state.sortDirection === 'asc' ? 'desc' : 'asc')
        : 'asc'
    }, () => {
      this.dataInit();
    });
  }

  static aggregateFn(symbol: string, a: number, b: number) {
    switch (symbol) {
      case '=':
        return a === b;
      case '<':
        return a < b;
      case '>':
        return a > b;
      case '<=':
        return a <= b;
      case '>=':
        return a >= b;
    }
  };

  columnSearchHandler(colIdx: number, value: string): void {
    this.state.originalDataSource.columns[colIdx].value = value;

    this.setState({
      originalDataSource: this.state.originalDataSource
    }, () => {
      this.dataInit();
    });
  }

  updateAggregateSelect(colIdx: number, value: string): void {
    this.state.originalDataSource.columns[colIdx].condition = value;

    this.setState({
      originalDataSource: this.state.originalDataSource
    }, () => {
      this.columnSearchHandler(colIdx, this.state.originalDataSource.columns[colIdx].value);
    });
  }

  addColumnWidth(colIdx: number, width: number): Object {
    const style = { width: `${width}px` };

    if (this.state.originalDataSource.columns.length > 0) {
      if (colIdx === 0) {
        return { ...style, left: 0 };
      }

      if (colIdx === 1) {
        return { ...style, left: `${this.state.originalDataSource.columns[0].width}px` };
      }

      if (colIdx === 2) {
        return {
          ...style,
          left: `${this.state.originalDataSource.columns[0].width + this.state.originalDataSource.columns[1].width}px`
        };
      }
    }

    return style;
  }

  addRowColumnWidth(rowStr: string): Object {
    const colIdx = this.state.dataSource.columns.findIndex((col) => col.id === rowStr);
    const colInfo = this.state.dataSource.columns[colIdx];

    const style = { width: `${colInfo.width}px` };

    if (this.state.originalDataSource.columns.length > 0) {
      if (colIdx === 0) {
        return { ...style, left: 0 };
      }

      if (colIdx === 1) {
        return { ...style, left: `${this.state.originalDataSource.columns[0].width}px` };
      }

      if (colIdx === 2) {
        return {
          ...style,
          left: `${this.state.originalDataSource.columns[0].width + this.state.originalDataSource.columns[1].width}px`
        };
      }
    }

    return style;
  }

  onUpdateVisibilityCheck(colIdx: number): void {
    this.state.measureVisibilityList[colIdx].isVisible =
      !this.state.measureVisibilityList[colIdx].isVisible;
    this.state.originalDataSource.columns[colIdx].isVisible =
      !this.state.originalDataSource.columns[colIdx].isVisible;

    this.setState({
      originalDataSource: this.state.originalDataSource,
      measureVisibilityList: this.state.measureVisibilityList
    }, () => {
      this.getVisibilityList();
    });
  }

  checkVisibilitySaveButton(): boolean {
    return this.state.measureVisibilityList
      .every((measure) => measure.isVisible === measure.prevCheck);
  }

  updateMeasureVisibility(): void {
    const payload: IMeasureVisibilityPayload = {
      visibility: this.state.measureVisibilityList
        .map((measure) => {
          return {
            name: measure.name,
            isVisible: measure.isVisible
          };
        })
    };
    const params = {
      userId: sessionStorage.getItem('userName'),
      planId: GetPlanId(this.props.history.location.pathname)
    };

    UpdateMeasureVisibility(payload, params)
      .then((res) => {
        if (!!res) {
          this.setState({
            isLoading: true,
            isModalOpened: true
          }, () => {
            this.getManageProduct();
          });
        }
      });
  }

  handleStart(e, col: string): void {
    let iniMouse = e.clientX;
    let iniSize = document.getElementById(`${col}`)?.offsetWidth;

    this.setState({
      drag: { iniSize: iniSize || 0, iniMouse }
    });
  }

  handleMove(e, col: string, colIdx: number): void {
    if (e.clientX) {
      let iniMouse = this.state.drag.iniMouse;
      let iniSize = this.state.drag.iniSize;
      let endMouse = e.clientX;

      let endSize = iniSize + (endMouse - iniMouse);
      const colEle = document.getElementById(`${col}`)!;
      const tableEle = document.getElementById('manage-product-table')!;

      if (endSize < 120) {
        return;
      }

      colEle.style.width = `${endSize}px`;
      tableEle.style.width = `${this.state.width + endSize}px`;

      this.state.dataSource.columns[colIdx].width = endSize;
      this.setState({ dataSource: this.state.dataSource });
    }
  }

  updateColWidth(): void {
    const payload: IColWidthPayload = {
      widthData: this.state.dataSource.columns
        .map((measure) => {
          return {
            name: measure.name,
            width: measure.width
          };
        })
    };
    const params = {
      userId: sessionStorage.getItem('userName'),
      planId: GetPlanId(this.props.history.location.pathname)
    };
    this.setState({ isLoading: true });

    UpdateColWidth(payload, params)
      .then((res) => {
        if (!!res) {
          this.setState({
            isModalOpened: true
          }, () => {
            this.getManageProduct();
          });
        }
      });
  }

  onColumnDrag(position: number): void {
    this.setState({ draggedPosition: position });
  }

  onColumnDrop(position: number): void {
    const draggedPosition = this.state.draggedPosition;
    const droppedPosition = position;

    const draggedIndex = this.state.originalDataSource.columns.findIndex((col) => col.position === draggedPosition);
    const droppedIndex = this.state.originalDataSource.columns.findIndex((col) => col.position === droppedPosition);

    this.state.originalDataSource.columns[draggedIndex].position = droppedPosition;
    this.state.originalDataSource.columns[droppedIndex].position = draggedPosition;
    this.state.originalDataSource.columns.sort((x, y) => x.position - y.position);

    this.dataInit();
  }

  updateColSequence(): void {
    const payload = this.state.originalDataSource.columns.map((col) => {
      return {
        id: col.id,
        position: col.position,
      };
    });

    const params = {
      userId: sessionStorage.getItem('userName'),
      planId: GetPlanId(this.props.history.location.pathname),
    };

    this.props.onUpdateStart();

    UpdateColSequence(payload, params)
      .then((res) => {
        if (!!res) {
          this.setState({
            isModalOpened: true,
            currentPage: 1
          }, () => {
            this.props.onUpdateEnd();
            this.props.UpdateEditRestriction(false);
          });
        }
      });
  }

  render(): React.ReactNode {
    if (this.state.isLoading) {
      return null;
    }

    return (
      <React.Fragment>

        <Card className="section-header-card">
          <Row>
            <Col md={6} className="align-self-center">

              <div className="d-flex">
                <p className="table-title">{this.props.filterData.level} -
                  <span className="sub-title"> {this.props.filterData.products}</span>
                </p>
                <img src={InfoIcon} alt="info-icon" className="cursor-pointer"/>
              </div>

            </Col>

            <Col md={6} className="align-self-center text-right">
              <Tooltip
                content={'Resize column width'}
                direction={'top'}
              >
                <i
                  className="mdi mdi-arrow-expand-horizontal font-size-18 cursor-pointer px-2"
                  style={{ verticalAlign: 'middle' }}
                  onClick={() => this.updateColWidth()}
                />
              </Tooltip>

              <Tooltip
                content={'Save Column Sequence'}
                direction={'top'}
              >
                <i
                  className="ri-drag-drop-line font-size-18 cursor-pointer px-2"
                  style={{ verticalAlign: 'middle' }}
                  onClick={() => this.updateColSequence()}
                />
              </Tooltip>

              <Dropdown
                isOpen={this.state.isVisibilityMenuOpened}
                toggle={this.toggleVisibilityMenu}
                className="d-inline-block user-dropdown px-2"
              >
                <Tooltip
                  content={'Measure Visibility Setting'}
                  direction={'top'}
                >
                  <DropdownToggle tag="button" className="btn action-btn waves-effect p-0">
                    <i className="ri-list-settings-line font-size-18"/>
                  </DropdownToggle>
                </Tooltip>

                <DropdownMenu className="custom-dropdown-menu dropdown-menu-end">
                  <React.Fragment>
                    <div className="dropdown-menu-box-sm">
                      {
                        this.state.measureVisibilityList.map((measure, mIdx: number) => (
                          <React.Fragment key={mIdx}>
                            {
                              ['active_flag', 'product_code', 'product_description'].indexOf(measure.id) === -1 &&
                              <DropdownItem
                                toggle={false}
                                className={`${(measure.isVisible !== measure.prevCheck) && 'dropdown-item-changed'}`}
                              >
                                <div className="d-flex align-items-center">
                                  <Input
                                    id={mIdx.toString()}
                                    type="checkbox"
                                    checked={measure.isVisible}
                                    onChange={() => this.onUpdateVisibilityCheck(mIdx)}
                                    className="me-2 flex-shrink-0"
                                  />
                                  <Label for={mIdx.toString()} className="cursor-pointer mb-0">
                                    {measure.name}
                                  </Label>
                                </div>
                              </DropdownItem>
                            }
                            {
                              this.state.measureVisibilityList.length - 1 !== mIdx &&
                              [0, 1].indexOf(mIdx) === -1 &&
                              <DropdownItem divider/>
                            }
                          </React.Fragment>
                        ))
                      }
                    </div>
                    <div className="py-1 text-right">
                      <Button
                        color={'primary'}
                        disabled={this.checkVisibilitySaveButton()}
                        className="btn btn-sm btn-primary ms-3 mx-3"
                        onClick={() => this.updateMeasureVisibility()}
                      >
                        Save
                      </Button>
                    </div>
                  </React.Fragment>
                </DropdownMenu>
              </Dropdown>

              <span className="ph-count mx-2">PH SKU : {this.getPHCount()}</span>

              <Button
                color={'primary'}
                className="btn btn-sm btn-primary ms-3 mx-3"
                disabled={!this.saveButtonValidity()}
                onClick={() => this.saveHandler()}
              >
                Save
              </Button>

              <Tooltip
                content={'More'}
                direction={'top'}
              >
                <Dropdown
                  isOpen={this.state.isMoreMenuOpened}
                  toggle={this.toggleMoreMenu}
                  className="d-inline-block user-dropdown"
                >
                  <DropdownToggle tag="button" className="btn action-btn waves-effect p-0">
                    <img src={MoreIcon} alt="more-icon"/>
                  </DropdownToggle>

                  <DropdownMenu className="dropdown-menu-end">
                    <DropdownItem disabled={this.enableDisableAssignAllAndAssignActiveBtn()}
                                  onClick={() => this.updateActiveAll()}>Assign All</DropdownItem>
                    <DropdownItem divider/>
                    <DropdownItem disabled={this.enableDisableAssignAllAndAssignActiveBtn()}
                                  onClick={() => this.initialiseActive()}>Assign Active</DropdownItem>
                    <DropdownItem divider/>
                    <DropdownItem disabled={!this.saveActiveValidity()}
                                  onClick={() => this.initialiseActiveAndInitializeRunRateView()}>Confirm Assign And Initialize RR View</DropdownItem>
                    <DropdownItem divider/>
                    <DropdownItem
                      disabled={!this.saveActiveValidity()}
                      onClick={() => this.updateActive()}
                    >
                      Confirm Assign
                    </DropdownItem>
                    <DropdownItem divider/>
                    <DropdownItem onClick={() => this.navigateToProductCatalog()}>Assign SKU to PH</DropdownItem>
                    <DropdownItem onClick={() => this.UnassignActiveAll()}>Unassign All</DropdownItem>
                    <DropdownItem onClick={() => this.copyWeightAndActiveStatus()}>Copy Assign Status & Weight from
                      LQ</DropdownItem>
                    <DropdownItem divider/>
                  </DropdownMenu>
                </Dropdown>
              </Tooltip>

            </Col>
          </Row>
        </Card>

        <div className="table-responsive table-outline custom-table sticky-table">
          <Table id="manage-product-table" className="mb-0" style={{ tableLayout: 'fixed' }}>
            <thead style={{ height: '150px' }}>
              <tr>
              {
                this.state.dataSource.columns.map((column, colIdx) => (
                  <React.Fragment key={colIdx}>
                    {
                      column.isVisible &&
                      <th
                        id={column.id}
                        data-position={column.position}
                        draggable="true"
                        onDragStart={() => this.onColumnDrag(column.position)}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={() => this.onColumnDrop(column.position)}
                        className={`${colIdx < 3 ? styles['sticky-col'] : 'position-relative'} cursor-pointer`}
                        style={
                          Object.assign(
                            {},
                            this.addColumnWidth(colIdx, column.width),
                            { cursor: 'grab' }
                          )
                        }
                      >
                        <div
                          className="dragger-line"
                          draggable={true}
                          onDragStart={(e) => this.handleStart(e, column.id)}
                          onDrag={(e) => this.handleMove(e, column.id, colIdx)}
                        />
                        <span onClick={() => this.doSort(column.id)}>
                            {column.name}
                          {
                            this.state.sortKey === column.id &&
                            <span className="px-1">{this.state.sortDirection === 'asc' ? '↑' : '↓'}</span>
                          }
                          </span>
                      </th>
                    }
                  </React.Fragment>
                ))
              }
              <th style={{ width: '50px' }}/>
            </tr>
            <tr>
              {
                this.state.dataSource.columns.map((column, colIdx: number) => (
                  <React.Fragment key={colIdx}>
                    {
                      column.isVisible &&
                      <th
                        className={`${colIdx < 3 ? styles['sticky-col'] : ''}`}
                        style={{
                          ...this.addColumnWidth(colIdx, column.width),
                          top: '58px',
                        }}
                      >
                        {
                          column.id !== 'active_flag'
                            ? <React.Fragment>
                              {
                                [
                                  'active_flag',
                                  'product_code',
                                  'product_description',
                                  'sku_type',
                                  'reference_sku_code',
                                  'actual_sku',
                                  'prod_level3_code',
                                  'prod_level4_code',
                                  'brand_level2_desc',
                                  'new_product_date',
                                  'discontinued_date',
                                  'graphics',
                                  'graphics_memory',
                                  'processor',
                                  'storage_size',
                                ].indexOf(column.id) === -1 &&
                                <select
                                  className="aggregate-select"
                                  style={{ width: '25px' }}
                                  onChange={(e) => this.updateAggregateSelect(colIdx, e.target.value)}
                                >
                                  <option value="=">=</option>
                                  <option value="<">&lt;</option>
                                  <option value=">">&gt;</option>
                                  <option value="<=">≤</option>
                                  <option value=">=">≥</option>
                                </select>
                              }
                              <input
                                type={'text'}
                                className="editable-input"
                                style={{
                                  width: [
                                    'active_flag',
                                    'product_code',
                                    'product_description',
                                    'sku_type',
                                    'reference_sku_code',
                                    'actual_sku',
                                    'prod_level3_code',
                                    'prod_level4_code',
                                    'brand_level2_desc',
                                    'new_product_date',
                                    'discontinued_date',
                                    'graphics',
                                    'graphics_memory',
                                    'processor',
                                    'storage_size',
                                  ].indexOf(column.id) !== -1
                                    ? '100%'
                                    : '70%',
                                  textAlign: 'left'
                                }}
                                onChange={(e) => this.columnSearchHandler(colIdx, e.target.value)}
                                value={column.value}
                              />
                            </React.Fragment>
                            : ''
                        }
                      </th>
                    }
                  </React.Fragment>
                ))
              }
              <th style={{ width: '50px' }}/>
            </tr>
            </thead>
            <tbody>
            {
              this.state.dataSource.rows.map((row, rowIdx: number) => (
                <tr key={rowIdx}>
                  <td
                    className={`text-center ${styles['sticky-col']}`}
                    style={Object.assign(
                      { zIndex: 99 },
                      this.addColumnWidth(0, this.state.dataSource.columns[0].width)
                    )}
                  >
                    {
                      !row.isNew
                        ? <Input
                          key={rowIdx}
                          type="checkbox"
                          checked={row.isChecked}
                          disabled={row.allowdeactive ? false : row.active_flag}
                          onChange={() => this.onUpdateCheckbox(rowIdx)}
                        />
                        : ''
                    }
                  </td>
                  {
                    this.state.dataSource.columns
                      .filter((col) => col.isVisible && col.id !== 'active_flag')
                      .map((col, colIdx: number) => (
                        <td
                          key={colIdx}
                          className={`${typeof row[col.id] === 'string' ? 'text-left' : 'text-right'}
                          ${[0, 1].indexOf(colIdx) !== -1 && styles['sticky-col']}`}
                          style={Object.assign(
                            { zIndex: 99 },
                            this.addRowColumnWidth(col.id),
                            ManageProductTable.addColumnStyle(col.id)
                          )}
                        >
                          {
                            !row.isNew
                              ?
                              <React.Fragment>
                                {['lqActuals$', 'lyActuals$', 'lyMargin$'].indexOf(col.id) !== -1 ? '$' : ''}
                                {
                                  typeof row[col.id] === 'number'
                                    ? NumberWithCommas(row[col.id])
                                    : (
                                      ['newproductdate', 'discontinueddate'].indexOf(col.id) !== -1
                                        ? (!!row[col.id] ? row[col.id].split(' ')[0] : '')
                                        : row[col.id]
                                    )
                                }
                                {['yMarginPer'].indexOf(col.id) !== -1 ? '%' : ''}
                              </React.Fragment>
                              : (
                                ['product_description'].indexOf(col.id) === -1
                                  ? row[col.id]
                                  : <input
                                    type={'text'}
                                    className="editable-input"
                                    onChange={(e) => this.updateList(e, rowIdx, col.id)}
                                    value={row[col.id]}
                                    style={{ width: '100%', textAlign: 'left' }}
                                  />
                              )
                          }
                        </td>
                      ))
                  }
                  <td className="d-flex justify-content-center">
                    {
                      !row.isNew
                        ? <button
                          disabled={this.checkLYLQValue(rowIdx)}
                          className="btn ms-3 mx-3 action-icon add"
                          onClick={() => this.addRow(rowIdx)}
                        >
                          <i className="ri ri-add-line"/>
                        </button>
                        : <button
                          className="btn ms-3 mx-3 action-icon remove"
                          onClick={() => this.removeRow(rowIdx)}
                        >
                          <i className="ri ri-subtract-line"/>
                        </button>
                    }
                  </td>
                </tr>
              ))
            }
            </tbody>
          </Table>
        </div>

        <Row>
          <Col md={12}>
            <Pagination
              currentPage={this.state.currentPage}
              totalCount={this.state.itemsCount}
              pageSize={this.state.itemsPerPage}
              onPageChange={(page) => this.handlePageChange(page)}
            />
          </Col>
        </Row>

        <SuccessModal
          isOpen={this.state.isModalOpened}
          toggle={() => this.setState({ isModalOpened: !this.state.isModalOpened })}
          message={'Data saved successfully !!!'}
        />

      </React.Fragment>
    );
  }
}

const mapStateToProps = (state: any): StateProps => {
  return {
    ...state.EditRestriction,
    filterData: state.FilterData
  };
};

export default connect<StateProps, DispatchProps, OwnProps>
(mapStateToProps, {
  UpdateEditRestriction
})(withRouter(ManageProductTable));
